/**
 * Tool Performance Monitoring Panel
 *
 * Displays real-time tool execution stats from GET /v1/admin/tools/perf.
 * Summary cards + sortable table of per-tool metrics.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  RefreshCw,
  RotateCcw,
  Activity,
  AlertTriangle,
  Gauge,
  Zap,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { fetchToolPerformance, resetToolPerformance, type ToolPerfStats } from '@/lib/api/tools';
import { useI18n } from '@/i18n';

type SortField = 'name' | 'calls' | 'errors' | 'avgMs' | 'maxMs' | 'errorRate';
type SortDir = 'asc' | 'desc';

interface ToolRow {
  name: string;
  stats: ToolPerfStats;
  isSlow: boolean;
}

export function ToolPerformancePanel() {
  const { messages } = useI18n();
  const t = messages.toolManagement.perf;
  const queryClient = useQueryClient();

  const [sortField, setSortField] = useState<SortField>('calls');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tool-perf'],
    queryFn: () => fetchToolPerformance(),
    refetchInterval: 30_000,
  });

  const resetMutation = useMutation({
    mutationFn: resetToolPerformance,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tool-perf'] }),
  });

  const handleReset = useCallback(() => {
    if (window.confirm(t.resetConfirm)) {
      resetMutation.mutate();
    }
  }, [t.resetConfirm, resetMutation]);

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }, [sortField]);

  const rows = useMemo<ToolRow[]>(() => {
    if (!data?.tools) return [];
    const slowSet = new Set(data.slow_tools ?? []);
    const list = Object.entries(data.tools).map(([name, stats]) => ({
      name,
      stats,
      isSlow: slowSet.has(name),
    }));

    list.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'calls': cmp = a.stats.call_count - b.stats.call_count; break;
        case 'errors': cmp = a.stats.error_count - b.stats.error_count; break;
        case 'avgMs': cmp = a.stats.avg_ms - b.stats.avg_ms; break;
        case 'maxMs': cmp = a.stats.max_ms - b.stats.max_ms; break;
        case 'errorRate': cmp = a.stats.error_rate - b.stats.error_rate; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [data, sortField, sortDir]);

  const fmtMs = (ms: number) => ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
  const fmtPct = (rate: number) => `${(rate * 100).toFixed(1)}%`;

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-blue-600" />
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <AlertTriangle className="w-8 h-8 text-red-400 mb-3" />
        <p className="font-medium text-gray-700">{t.loadError}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          {t.refresh}
        </Button>
      </div>
    );
  }

  const isEmpty = !data || data.total_tools_tracked === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t.title}</h2>
          <p className="text-sm text-gray-500">{t.description}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t.refresh}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={isEmpty || resetMutation.isPending}
            className="flex items-center gap-1.5 text-red-600 hover:text-red-700"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {t.reset}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Activity className="w-5 h-5 text-blue-600" />}
          label={t.totalCalls}
          value={data?.total_calls ?? 0}
        />
        <SummaryCard
          icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
          label={t.totalErrors}
          value={data?.total_errors ?? 0}
          highlight={!!data && data.total_errors > 0}
        />
        <SummaryCard
          icon={<Gauge className="w-5 h-5 text-emerald-600" />}
          label={t.trackedTools}
          value={data?.total_tools_tracked ?? 0}
        />
        <SummaryCard
          icon={<Zap className="w-5 h-5 text-amber-500" />}
          label={t.slowTools}
          value={data?.slow_tools?.length ?? 0}
          highlight={!!data && (data.slow_tools?.length ?? 0) > 0}
        />
      </div>

      {/* Table */}
      {isEmpty ? (
        <Card className="p-12 text-center">
          <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t.noData}</p>
          <p className="text-gray-400 text-sm mt-1">{t.noDataHint}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <Th field="name" label={t.toolName} onSort={handleSort} SortIcon={SortIcon} />
                  <Th field="calls" label={t.calls} onSort={handleSort} SortIcon={SortIcon} align="right" />
                  <Th field="errors" label={t.errors} onSort={handleSort} SortIcon={SortIcon} align="right" />
                  <Th field="avgMs" label={t.avgMs} onSort={handleSort} SortIcon={SortIcon} align="right" />
                  <Th field="maxMs" label={t.maxMs} onSort={handleSort} SortIcon={SortIcon} align="right" />
                  <Th field="errorRate" label={t.errorRate} onSort={handleSort} SortIcon={SortIcon} align="right" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.lastError}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(({ name, stats, isSlow }) => (
                  <tr key={name} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                      <span className="text-gray-900">{name}</span>
                      {isSlow && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                          {t.slow}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{stats.call_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className={stats.error_count > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                        {stats.error_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtMs(stats.avg_ms)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtMs(stats.max_ms)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className={stats.error_rate > 0.1 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                        {fmtPct(stats.error_rate)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 max-w-[200px] truncate" title={stats.last_error ?? ''}>
                      {stats.last_error ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, highlight }: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="shrink-0">{icon}</div>
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className={`text-xl font-semibold tabular-nums ${highlight ? 'text-red-600' : 'text-gray-900'}`}>
            {value.toLocaleString()}
          </p>
        </div>
      </div>
    </Card>
  );
}

function Th({ field, label, onSort, SortIcon, align }: {
  field: SortField;
  label: string;
  onSort: (f: SortField) => void;
  SortIcon: React.ComponentType<{ field: SortField }>;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <SortIcon field={field} />
      </span>
    </th>
  );
}
